import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetWipByCaseReportQuery } from 'store/api/reportingOperations';
import { WipByCaseRow, WipByCaseTotals } from 'types/reporting/operations';
import { ReportPage } from 'types/reporting/reportPage';
import { t } from 'utils/getTranslations';

import {
    ReportCaseLink,
    ReportContactLink,
    ReportDateCell,
    ReportEmployeeLink,
    ReportNumberCell,
    TONED_COLUMN_META,
    tonedCell,
    ToneRule,
    GREEN,
    YELLOW,
    RED,
} from '../reportCells';
import { caseFilter, clientFilter, employeeFilter, searchSelectors } from '../reportFilters';
import { inBaseCurrency, useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';

type Row = WipByCaseRow;
type Totals = WipByCaseTotals;

const REPORT_KEY = 'wip-by-case';

function buildFilterConfig(): FilterConfig {
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        quickFilters: [],
        searchBarSelectors: searchSelectors('caseNumber', 'caseName', 'clientName'),
        filters: [
            clientFilter(),
            caseFilter(),
            employeeFilter('supervisingPartnerId', 'reporting.filter.supervising-partner'),
            employeeFilter('responsibleLawyerId', 'reporting.filter.responsible-lawyer'),
        ],
    };
}

function figureColumn(
    id: keyof Totals & keyof Row,
    header: string,
    rows: Row[] | undefined,
    rule: ToneRule,
    widthWeight = 0.7
): DataTableColumnDef<Row, Totals> {
    return {
        id,
        accessorKey: id,
        header,
        meta: {
            ...TONED_COLUMN_META,
            widthWeight,
            minPx: 120,
            textAlign: 'right',
            nullHandling: 'nullsLast',
            renderTotal: (totals) => <ReportNumberCell value={totals?.[id] as number | null | undefined} />,
        },
        cell: tonedCell(rows, (row) => row[id] as number | null, rule),
    };
}

function buildColumns(rows: Row[] | undefined, currency: string | null): DataTableColumnDef<Row, Totals>[] {
    return [
        {
            id: 'clientNumber',
            accessorKey: 'clientNumber',
            header: t('reporting.column.client-number'),
            meta: { widthWeight: 0.5, minPx: 90 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientNumber}</ReportContactLink>
            ),
        },
        {
            id: 'clientName',
            accessorKey: 'clientName',
            header: t('reporting.column.client-name'),
            meta: { widthWeight: 1.2, minPx: 160 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientName}</ReportContactLink>
            ),
        },
        {
            id: 'caseNumber',
            accessorKey: 'caseNumber',
            header: t('reporting.column.case-number'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId} wrapLabel>
                    {row.original.caseNumber}
                </ReportCaseLink>
            ),
        },
        {
            id: 'caseName',
            accessorKey: 'caseName',
            header: t('reporting.column.case-name'),
            meta: { widthWeight: 1.4, minPx: 180 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId}>{row.original.caseName}</ReportCaseLink>
            ),
        },
        {
            id: 'supervisingPartner',
            accessorKey: 'supervisingPartner',
            header: t('reporting.column.supervising-partner'),
            meta: { widthWeight: 0.9, minPx: 140 },
            cell: ({ row }) => (
                <ReportEmployeeLink employeeId={row.original.supervisingPartnerId}>
                    {row.original.supervisingPartner}
                </ReportEmployeeLink>
            ),
        },
        {
            id: 'responsibleLawyer',
            accessorKey: 'responsibleLawyer',
            header: t('reporting.column.responsible-lawyer'),
            meta: { widthWeight: 0.9, minPx: 140 },
            cell: ({ row }) => (
                <ReportEmployeeLink employeeId={row.original.responsibleLawyerId}>
                    {row.original.responsibleLawyer}
                </ReportEmployeeLink>
            ),
        },
        {
            id: 'lastTimecardDate',
            accessorKey: 'lastTimecardDate',
            header: t('reporting.column.last-timecard-date'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => <ReportDateCell value={row.original.lastTimecardDate} />,
        },
        figureColumn('billableHours', t('reporting.column.billable-hours'), rows, YELLOW),
        figureColumn('invoicedHours', t('reporting.column.invoiced-hours'), rows, GREEN),
        figureColumn('wipHours', t('reporting.column.wip-hours'), rows, RED),
        figureColumn('wipYtdHours', t('reporting.column.wip-ytd-hours'), rows, RED),
        figureColumn(
            'billableAmount',
            inBaseCurrency(t('reporting.column.billable-amount'), currency),
            rows,
            YELLOW,
            0.8
        ),
        figureColumn(
            'invoicedAmount',
            inBaseCurrency(t('reporting.column.invoiced-amount'), currency),
            rows,
            GREEN,
            0.8
        ),
        figureColumn('wipAmount', inBaseCurrency(t('reporting.column.wip-amount'), currency), rows, RED, 0.8),
        figureColumn('wipYtdAmount', inBaseCurrency(t('reporting.column.wip-ytd-amount'), currency), rows, RED, 0.8),
    ];
}

export function WipByCaseReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetWipByCaseReportQuery();
    const filterConfig = useMemo(() => buildFilterConfig(), []);
    const currency = useReportBaseCurrency(data?.totals?.baseCurrency ?? data?.content[0]?.baseCurrency);
    const columns = useMemo(() => buildColumns(data?.content, currency), [data, currency]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)} baseCurrency={currency}>
            <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                data={data}
                belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                isLoading={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                columns={columns}
                columnConfigId={`report-${REPORT_KEY}`}
                enableColumnPicker
                enableColumnReorder
                enableColumnResize
                pluralTextId="reporting.cases"
                singularTextId="reporting.case"
                noEntriesId="reporting.no-rows"
                noEntriesBodyId="reporting.no-rows-body"
            />
        </ReportLayout>
    );
}
